import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useAuthToken,
    useRequestUrl,
    useCourtsByAgency,
    useAgencyCourt,
    useFullCourtData,
    useLogEntry,
    useCurrentUser,
    useNearPasswordExpiration,
} from "../context/contextHooks";
import {
    getCourtFromCourtId, checkMultipleParties, getPartyData, toPaidPlan, toPaymentBalance
} from "./lookupFunctions";
import { validateAddBalance, validateAddPlan, getCourtAmount, validatePayment, getPaymentRequest } from "./selectedFunctions";
import API from '../http/API';
import { useSxStyles } from "../styles/sxStyles";
import Box from '@mui/material/Box';
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from '@mui/material/useMediaQuery'
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from "@mui/material/CircularProgress";
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Select from '@mui/material/Select';
import Grid2 from '@mui/material/Grid2';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Drawer from '@mui/material/Drawer';
import Button from "@mui/material/Button";
import Search from "./Search";
import PartyList from './PartyList';
import PlanList from "./PlanList";
import BalanceList from "./BalanceList";
import NameList from "./NameList";
import SelectedBalances from "./SelectedBalances";
import CompletePayment from '../completePayment/CompletePayment';
import ManageWidget from '../completePayment/ManageWidget';
import PaymentDetails from '../completePayment/PaymentDetails';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EnterCall from "../callLogging/EnterCall";
import EnterResult from "../callLogging/EnterResult";
import UpdateUserPassword from "../users/UpdateUserPassword";
import AlertMessage from "../utils/AlertMessage";

const BalanceLookup = () => {
    const navigate = useNavigate();
    const [authToken,] = useAuthToken();
    const [requestUrl,] = useRequestUrl();
    const [courtsByAgency,] = useCourtsByAgency();
    const [agencyCourt, setAgencyCourt] = useAgencyCourt();
    const [,setCourtData] = useFullCourtData();
    const [courts, setCourts] = useState([]);
    const isNearPasswordExpiration = useNearPasswordExpiration();
    const [currentUser,] = useCurrentUser();
    const [logEntry, setLogEntry] = useLogEntry();
    const [newCall, setNewCall] = useState(false);
    const classes = useSxStyles;
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [loading, setLoading] = useState(false);
    const [court, setCourt] = useState(null);
    const [showSelected, setShowSelected] = useState(false);
    const [openSelectedClicked, setOpenSelectedClicked] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);
    const [balanceData, setBalanceData] = useState(null);
    const [partyData, setPartyData] = useState(null);
    const [nameSearch, setNameSearch] = useState(false);
    const [nameData, setNameData] = useState(null);
    const [displayData, setDisplayData] = useState(null);
    const [selectedBalances, setSelectedBalances] = useState([]);
    const [selectedPlans, setSelectedPlans] = useState([]);
    const [feeResponse, setFeeResponse] = useState(null);
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [paymentResult, setPaymentResult] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openReceipt, setOpenReceipt] = useState(false);

    const clearMessages = () => {
        setActionResponse(null);
    }

    const clearData = () => {
        setBalanceData(null);
        setPartyData(null);
        setNameData(null);
        setDisplayData(null);
        setSelectedBalances([]);
        setSelectedPlans([]);
        setPaymentRequest(null);
        setPaymentResult(null);
    }

    const retrieveBalances = (balanceQuery) => {
        clearMessages();
        clearData();
        if (balanceQuery.lastName && court.interfaceUrl) {
            setNameSearch(true);
        } else {
            setNameSearch(false);
        }
        setLoading(true);
        const url = `${requestUrl}/getBalances`;
        API.postRetrieve(url,JSON.stringify(balanceQuery), 'getBalances', `Bearer ${authToken}`, setActionResponse);
    }

    const calculateFee = (selectedBals, selectedPlans) => {
        clearMessages();
        setLoading(true);
        const courtAmt = getCourtAmount(selectedBals, selectedPlans);
        const query = {
            courtId: court.courtId,
            paymentCategory: 'MOTO',
            paymentType: 'Card',
            courtAmount: courtAmt
        }
        const url = `${requestUrl}/calculateServiceFee`;
        API.postRetrieve(url, JSON.stringify(query), 'getProcessingFee', `Bearer ${authToken}`, setActionResponse);
    }

    const selectParty = (partyRec) => {
        const partyId = partyRec.balances[0].party.id;
        const plans = (balanceData.paymentPlans && balanceData.paymentPlans.length > 0) ? balanceData.paymentPlans.filter(p => p.balances[0].partyId === partyId) : null;
        const partyData = {
            balances: partyRec.balances,
            paymentPlans: plans
        }
        setDisplayData(partyData);
        setBalanceData(null);
    }

    const selectNameBalance = (balance) => {
        const query = {
            courtId: court.courtId,
            caseNumber: balance.caseNumber,
            dob: balance.party.dob,
            fromAdmin: true
        };
        retrieveBalances(query);
        setNameData(null);
    }

    const selectBalance = (balance) => {
        clearMessages();
        const pymtBal = toPaymentBalance(balance, court);
        const errors = validateAddBalance(pymtBal, selectedBalances, selectedPlans, court);
        if (errors && errors.length > 0) {
            setActionResponse({error: true, errorMessage: errors})
        } else {
            balance.selected = true;
            setSelectedBalances([...selectedBalances, pymtBal]);
        }
    }

    const selectPlan = (plan) => {
        clearMessages();
        const paidPln = toPaidPlan(plan);
        const errors = validateAddPlan(paidPln, selectedBalances, selectedPlans, court);
        if (errors && errors.length > 0) {
            setActionResponse({error: true, errorMessage: errors})
        } else {
            plan.selected = true;
            setSelectedPlans([...selectedPlans, paidPln]);
        }
    }

    const removeBalance = (balance) => {
        const refId = balance.refId;
        setSelectedBalances(selectedBalances.filter(bal => bal.refId !== refId));
        displayData.balances.forEach(bal => {if (bal.refId === refId) bal.selected = null});
    }

    const removePlan = (plan) => {
        const plnId = plan.planId;
        setSelectedPlans(selectedPlans.filter(pln => pln.planId !==plnId));
        displayData.paymentPlans.forEach(pln => {
            if (pln.planId === plnId) {
                pln.selected = null;
                pln.installments.forEach(i => i.checked = null);
            }});
    }

    const clearSelected = () => {
        selectedBalances.forEach(bal => removeBalance(bal));
        selectedPlans.forEach(pln => removePlan(pln));
        setSelectedBalances([]);
        setSelectedPlans([]);
    }

    const cancelPymt = () => {
        clearSelected();
        setFeeResponse(null);
        setPaymentRequest(null);
    }

    const somethingSelected = () => {
        return ((selectedBalances && selectedBalances.length > 0) || (selectedPlans && selectedPlans.length > 0));
    }

    const openSelected = () => {
        setOpenSelectedClicked(true);
    }

    const openPayment = () => {
        const errors = validatePayment(selectedBalances, court);
        if (errors && errors.length > 0) {
            setActionResponse({error: true, errorMessage: errors})
        } else {
            clearMessages();
            setPaymentRequest(getPaymentRequest(court, selectedBalances, selectedPlans, feeResponse));
        }
    }

    const toggleSelected = (open) =>
        (event) => {
            if (
                event.type === 'keydown' &&
                (event.key === 'Tab' || event.key === 'Shift')
            ) {
                return;
            }
            setShowSelected(open);
        }

    const handleOrg = (e) => {
        const orgId = e.target.value;
        if (orgId) {
            if (!court || court.courtId !== orgId) {
                const org = getCourtFromCourtId(orgId, courts);
                setCourt(org);
                clearMessages();
            }
        } else {
            setCourt(null);
            clearMessages();
        }
    }

    useEffect(() => {
        if (courtsByAgency && courtsByAgency.courts && courtsByAgency.courts.length > 0) {
            setCourts(courtsByAgency.courts);
        } else {
            setCourts([]);
        }
    }, [courtsByAgency])

    useEffect(() => {
        setCourtData({court: court});
    }, [court])

    useEffect(() => {
        if (loading && actionResponse) {
            if (!actionResponse.error) {
                if (actionResponse.requestType === 'getBalances') {
                    setBalanceData(actionResponse);
                } else if (actionResponse.requestType === 'getProcessingFee') {
                    setFeeResponse(actionResponse);
                }
            }
            setLoading(false);
        }
    }, [actionResponse])

    useEffect(() => {
        if (balanceData) {
            const hasMultiple = checkMultipleParties(balanceData);
            if (hasMultiple) {
                setPartyData(getPartyData(balanceData));
            } else if (nameSearch && balanceData.balances) {
                setNameData(balanceData.balances);
                setBalanceData(null);
                setNameSearch(false);
            } else {
                setDisplayData(balanceData);
                setBalanceData(null);
            }
        }
    }, [balanceData])

    useEffect(() => {
        if ((selectedBalances && selectedBalances.length > 0) || (selectedPlans && selectedPlans.length > 0)) {
            setShowSelected(true);
            calculateFee(selectedBalances, selectedPlans);
        } else {
            setFeeResponse({citepayFee: 0, courtAmount: 0, totalAmount: 0})
        }
        setOpenSelectedClicked(false);
    }, [selectedBalances, selectedPlans, openSelectedClicked])

    useEffect(() => {
        if (paymentRequest) {
            setOpenDialog(true);
        }
    }, [paymentRequest])

    useEffect(() => {
        if (paymentResult) {
            setOpenReceipt(true);
        }
    }, [paymentResult])

    useEffect(() => {
        if (!openReceipt && paymentResult) {
            clearSelected();
            clearData();
        }
    }, [openReceipt])

    useEffect(() => {
        if (!agencyCourt && courtsByAgency && courtsByAgency.courts && courtsByAgency.courts.length === 1) {
            setAgencyCourt(courtsByAgency.courts[0]);
        }
    }, [courtsByAgency, agencyCourt]);

    return (<>
        { paymentRequest && !paymentResult &&
            ( court?.neumoPayOnline
                ? <ManageWidget
                    openDialog={openDialog}
                    setOpenDialog={setOpenDialog}
                    paymentRequest={paymentRequest}
                    setPaymentRequest={setPaymentRequest}
                    setPaymentResult={setPaymentResult}
                    court={court}
                    feeResponse={feeResponse}
                    cancelPymt={cancelPymt}
                />
                : <CompletePayment
                    openDialog={openDialog}
                    setOpenDialog={setOpenDialog}
                    paymentRequest={paymentRequest}
                    setPaymentResult={setPaymentResult}
                    court={court}
                />
            )
        }
        { paymentResult &&
            <PaymentDetails
                paymentRequest={paymentRequest}
                paymentResult={paymentResult}
                openReceipt={openReceipt}
                setOpenReceipt={setOpenReceipt}
            />
        }
        <Typography variant='h4' component="h1" color='primary' sx={{marginTop: '1rem'}}>
            Pay Case/Account Balances
        </Typography>
        {currentUser && isNearPasswordExpiration ? <AlertMessage severity="warning" title="Password Expiration Warning" msg="Your password will expire soon. Reset the password at your earliest convenience." /> : null}
        { logEntry &&
            <Typography variant='h6' color='textSecondary'>
                Call from: {`${logEntry.callerFirstName} ${logEntry.callerLastName}`}
            </Typography>
        }
        <Box onClick={toggleSelected(false)} onKeyDown={toggleSelected(false)} role='presentation'>
            <Grid2
                container
                spacing={3}
                justifyContent="center"
            >
                <Backdrop open={loading} sx={classes.backdrop}>
                    <CircularProgress color='inherit' />
                </Backdrop>
                <Grid2
                    sx={classes.gridBottom}
                    size={{
                        xs: 12,
                        md: 5
                    }}>
                    { currentUser && currentUser.logCalls && !logEntry ?
                        <EnterCall currentUser={currentUser} setLogEntry={setLogEntry} />:
                        <>
                            { newCall ?
                                <EnterResult logEntry={logEntry} setLogEntry={setLogEntry} currentUser={currentUser} setNewCall={setNewCall} clearData={clearData} /> :
                                <>
                                    <Paper elevation={3} sx={classes.controlPanel}>
                                        <Grid2 size='12'>
                                            <FormControl
                                                component="fieldset"
                                                fullWidth
                                                sx={classes.formControl}
                                            >
                                                <FormLabel>
                                                    <Typography variant='subtitle1' color='primary' sx={{fontSize: '1.1rem'}}>Select Court/Agency</Typography>
                                                </FormLabel>
                                                <Select
                                                    name='court'
                                                    native variant='outlined'
                                                    fullWidth
                                                    onChange={handleOrg}
                                                    sx={{height: '2rem'}}
                                                >
                                                    <option key='none' value=''>None Selected</option>
                                                    {courts && courts.map((ct) => (
                                                        <option key={ct.courtId} value={ct.courtId}>{ct.courtName}</option>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            { court && (court.enableMiscFees || court.vtUnappliedPayments) &&
                                                <Box component='div' sx={{...classes.root, display: 'flex', flexDirection: 'row-reverse', marginTop: '1rem'}}>
                                                    { court.enableMiscFees &&
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<AddShoppingCartIcon/>}
                                                            sx={classes.button}
                                                            onClick={() => navigate('/miscFees')}
                                                        >
                                                            Pay Miscellaneous Fees
                                                        </Button>
                                                    }
                                                    { court.vtUnappliedPayments &&
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<ReceiptLongOutlinedIcon/>}
                                                            sx={classes.button}
                                                            onClick={() => navigate('/unapplied')}
                                                        >
                                                            Bond/Case Payment
                                                        </Button>
                                                    }
                                                </Box>
                                            }
                                        </Grid2>
                                        { court &&
                                            <Grid2 size={12}>
                                                <Search court={court} retrieveBalances={retrieveBalances} logEntry={logEntry} setNewCall={setNewCall} />
                                            </Grid2>
                                        }
                                    </Paper>
                                    { somethingSelected() && !showSelected &&
                                        <>
                                            <Box component='div' sx={{...classes.root, display: 'flex', flexDirection: 'row-reverse', margin: '1rem 3rem'}} >
                                                <Button
                                                    variant='contained'
                                                    fullWidth
                                                    color='primary'
                                                    sx={classes.squashedButton}
                                                    onClick={openSelected}
                                                >
                                                    See Selected Balances
                                                </Button>
                                            </Box>
                                        </>
                                    }
                                </>
                            }
                        </>
                    }
                </Grid2>
                <Grid2
                    sx={classes.gridBottom}
                    size={{
                        xs: 12,
                        md: 7
                    }}>
                    { actionResponse && actionResponse.errorMessage &&
                        <Alert severity='error'>
                            <AlertTitle>Error!</AlertTitle>
                            {actionResponse.errorMessage}
                        </Alert>
                    }
                    { partyData && !displayData &&
                        <PartyList partyData={partyData} court={court} selectParty={selectParty}/>
                    }
                    { nameData && !displayData &&
                        <NameList nameData={nameData} court={court} selectNameBalance={selectNameBalance} />
                    }
                    { displayData && displayData.paymentPlans && displayData.paymentPlans.length > 0 &&
                        <PlanList plans={displayData.paymentPlans} selectPlan={selectPlan} />
                    }
                    { displayData && displayData.balances &&
                        <BalanceList balances={displayData.balances} court={court} selectBalance={selectBalance} />
                    }
                    { displayData && !displayData.balances &&
                        <Alert severity='info' sx={{marginTop: '1rem'}}>
                            No balances retrieved using the criteria entered
                        </Alert>
                    }
                    <Drawer
                        anchor='right'
                        open={showSelected}
                        onClose={toggleSelected(false)}
                        slotProps={{
                            paper: {style: {width: smallScreen ? '60vw' : '55vw', margin: '1rem', padding: '1rem'}}
                        }}
                    >
                        <SelectedBalances
                            plans={selectedPlans}
                            balances={selectedBalances}
                            court={court}
                            removeBalance={removeBalance}
                            removePlan={removePlan}
                            feeResponse={feeResponse}
                            clearSelected={clearSelected}
                            openPayment={openPayment}
                            setShowSelected={setShowSelected}
                        />
                    </Drawer>
                </Grid2>
            </Grid2>
        </Box>
        <UpdateUserPassword />
    </>);
}

export default BalanceLookup;
